import React, { useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import DialogTemplate from '../../../layout/DialogTemplate';
import Button from '../../../common/inputs/Button';
import InputField from '../../../common/inputs/InputField';
import { adminApi, AdminRole, AdminUserInvite } from '../../../../services/api/admin';
import { useToast } from '../../../../contexts/ToastContext';

interface InviteUserDialogProps {
  open: boolean;
  orgId: string;
  roles: AdminRole[];
  onClose: () => void;
  onInvited: () => void;
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  orgId,
  roles,
  onClose,
  onInvited,
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSuccess, showError } = useToast();

  const handleClose = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setSelectedRoleIds([]);
    onClose();
  };

  const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedRoleIds(typeof value === 'string' ? value.split(',') : value);
  };

  const isValid = email.trim() && firstName.trim() && lastName.trim();

  const handleSave = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const invite: AdminUserInvite = {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role_ids: selectedRoleIds,
      };
      await adminApi.inviteUser(orgId, invite);
      showSuccess(`Einladung an ${email.trim()} wurde versendet`);
      onInvited();
      handleClose();
    } catch {
      showError('Einladung konnte nicht versendet werden');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogTemplate
      open={open}
      onClose={handleClose}
      title="User einladen"
      maxWidth="sm"
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSubmitting || !isValid}
          >
            {isSubmitting ? 'Sende...' : 'Einladung senden'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <InputField
          label="E-Mail"
          value={email}
          onChange={setEmail}
          placeholder="user@example.com"
          required
          size="small"
        />
        <InputField
          label="Vorname"
          value={firstName}
          onChange={setFirstName}
          placeholder="Max"
          required
          size="small"
        />
        <InputField
          label="Nachname"
          value={lastName}
          onChange={setLastName}
          placeholder="Mustermann"
          required
          size="small"
        />
        {roles.length > 0 && (
          <FormControl size="small" fullWidth>
            <InputLabel>Rollen</InputLabel>
            <Select
              multiple
              value={selectedRoleIds}
              onChange={handleRoleChange}
              input={<OutlinedInput label="Rollen" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const role = roles.find((r) => r.id === id);
                    return <Chip key={id} label={role?.name ?? id} size="small" />;
                  })}
                </Box>
              )}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </DialogTemplate>
  );
};

export default InviteUserDialog;
